import type {
  NewAccountsReportItem,
  PaysReportItem,
  ReceivedPaysReportItem,
} from '@/models/report/types';
import type { Country } from '@/models/country/types';
import { formatDateOnly, formatDateTime } from '@/lib/dateUtils';
import { parseNumber } from '@/lib/format';
import { currencySign, groupByCountry } from '@/lib/currency';
import { isProcessedPayment } from '@/lib/paymentStatus';

export type ExportCellValue = string | number;
export type ExportRow = Record<string, ExportCellValue>;

export interface ExportColumn {
  header: string;
  key: string;
  type?: 'money';
}

export interface ReportExportData {
  fileName: string;
  sheetName: string;
  title: string;
  columns: ExportColumn[];
  rows: ExportRow[];
  // Итоги по странам под таблицей: суммы в разных валютах не складываются.
  totals: ExportRow[];
}

export type CountryOf<T> = (item: T) => Country | undefined;

const CURRENCY_COLUMN: ExportColumn = { header: 'Валюта', key: 'currency' };

function buildCountryTotals<T>(
  items: T[],
  countryOf: CountryOf<T>,
  title: { key: string; text: string },
  amounts: Record<string, (item: T) => number>
): ExportRow[] {
  return groupByCountry(items, countryOf).map((group) => {
    const row: ExportRow = {
      [title.key]: `${title.text} · ${group.label}`,
      currency: group.currency,
    };
    for (const [key, amountOf] of Object.entries(amounts)) {
      row[key] = group.items.reduce((sum, item) => sum + amountOf(item), 0);
    }
    return row;
  });
}

export function buildNewClientsExportData(
  items: NewAccountsReportItem[],
  countryOf: CountryOf<NewAccountsReportItem>
): ReportExportData {
  const columns: ExportColumn[] = [
    { header: 'Дата регистрации', key: 'date_reg' },
    { header: 'Аккаунт', key: 'account_name' },
    { header: 'Логин', key: 'login' },
    { header: 'Телефон', key: 'phone' },
    { header: 'Страна', key: 'country_name' },
    { header: 'Тип', key: 'account_type_name' },
    { header: 'Платный с', key: 'date_payed_tariff' },
    { header: 'Камер', key: 'count_cameras' },
    { header: 'Статус', key: 'status_name' },
    { header: 'Абонплата', key: 'full_abon_plata', type: 'money' },
    { header: 'Абонплата камер', key: 'all_cameras_abon_plata', type: 'money' },
    CURRENCY_COLUMN,
  ];

  return {
    fileName: 'new_clients',
    sheetName: 'Новые клиенты',
    title: 'Новые клиенты',
    columns,
    rows: items.map((item) => ({
      date_reg: formatDateOnly(item.date_reg),
      account_name: item.account_name,
      login: item.login,
      phone: item.phone,
      country_name: item.country_name,
      account_type_name: item.account_type_name,
      date_payed_tariff: formatDateOnly(item.date_payed_tariff),
      count_cameras: parseNumber(item.count_cameras),
      status_name: item.status_name,
      full_abon_plata: parseNumber(item.full_abon_plata),
      all_cameras_abon_plata: parseNumber(item.all_cameras_abon_plata),
      currency: currencySign(countryOf(item)),
    })),
    totals: buildCountryTotals(
      items,
      countryOf,
      { key: 'date_reg', text: 'Итого' },
      {
        full_abon_plata: (item) => parseNumber(item.full_abon_plata),
        all_cameras_abon_plata: (item) => parseNumber(item.all_cameras_abon_plata),
      }
    ),
  };
}

export function buildPaysExportData(
  items: PaysReportItem[],
  countryOf: CountryOf<PaysReportItem>
): ReportExportData {
  const columns: ExportColumn[] = [
    { header: 'Дата', key: 'date_pay' },
    { header: 'Тип', key: 'pay_type_name' },
    { header: 'Сумма', key: 'money', type: 'money' },
    CURRENCY_COLUMN,
    { header: 'Подтверждение', key: 'confirm_date' },
    { header: 'Статус', key: 'status_name' },
    { header: 'Телефон', key: 'phone' },
    { header: 'Логин', key: 'login' },
    { header: 'Клиент', key: 'account_name' },
  ];

  return {
    fileName: 'pays',
    sheetName: 'Платежи',
    title: 'Платежи',
    columns,
    rows: items.map((item) => ({
      date_pay: formatDateTime(item.date_pay),
      pay_type_name: item.pay_type_name,
      money: parseNumber(item.money),
      currency: currencySign(countryOf(item)),
      confirm_date: formatDateTime(item.confirm_date),
      status_name: item.status_name,
      phone: item.phone,
      login: item.login,
      account_name: item.account_name,
    })),
    // Как карточка «Общая сумма» на экране — только проведённые платежи.
    totals: buildCountryTotals(
      items.filter((item) => isProcessedPayment(item.status_name)),
      countryOf,
      { key: 'date_pay', text: 'Итого проведено' },
      { money: (item) => parseNumber(item.money) }
    ),
  };
}

export function buildReceivedPaysExportData(
  items: ReceivedPaysReportItem[],
  countryOf: CountryOf<ReceivedPaysReportItem>
): ReportExportData {
  const columns: ExportColumn[] = [
    { header: 'Аккаунт', key: 'id_account' },
    { header: 'Дата периода', key: 'date_start_period' },
    { header: 'Дата изменения', key: 'date_of_change' },
    { header: 'Клиент', key: 'account_name' },
    { header: 'Логин', key: 'login' },
    { header: 'Сумма', key: 'money', type: 'money' },
    CURRENCY_COLUMN,
    { header: 'Тип услуги', key: 'service_type_name' },
    { header: 'Камера', key: 'id_camera' },
  ];

  return {
    fileName: 'received_pays',
    sheetName: 'Отчёт по абонентской плате',
    title: 'Отчёт по абонентской плате',
    columns,
    rows: items.map((item) => ({
      id_account: item.id_account,
      date_start_period: formatDateTime(item.date_start_period),
      date_of_change: formatDateTime(item.date_of_change),
      account_name: item.account_name,
      login: item.login,
      money: parseNumber(item.money),
      currency: currencySign(countryOf(item)),
      service_type_name: item.service_type_name,
      id_camera: item.id_camera ?? '—',
    })),
    totals: buildCountryTotals(
      items,
      countryOf,
      { key: 'id_account', text: 'Итого' },
      { money: (item) => parseNumber(item.money) }
    ),
  };
}
